/**
 * RTDB Cache Store
 *
 * Zustand store for managing persistent RTDB listener data.
 * Stores data per-location so switching locations is instant (no re-fetch).
 *
 * This store works with RTDBListenerManager to provide:
 * - Per-location data caching
 * - Instant location switching (show cached data immediately)
 * - Delta-only updates (listeners stay connected)
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { AllDataMap, AnyRentalItem, RentalEquipmentItem } from '@/types';
import { RTDBListenerManager } from '../hooks/rtdb/RTDBListenerManager';
import type { Database } from 'firebase/database';
import { logger } from '../utils/logger';

// Types for organizing data
interface LocationDataCache {
  /** Raw items from RTDB */
  items: AnyRentalItem[];
  /** Organized by category (computed from items) */
  organized: AllDataMap;
  /** When this data was last updated */
  lastUpdated: number;
  /** Whether initial load has completed */
  isLoaded: boolean;
}

type BoardType = 'rentals' | 'stamps';

interface RTDBCacheState {
  // Per-board, per-location data cache
  rentalsCache: Record<string, LocationDataCache>;
  stampsCache: Record<string, LocationDataCache>;

  // Manager instances (one per board type)
  rentalsManager: RTDBListenerManager<AnyRentalItem> | null;
  stampsManager: RTDBListenerManager<AnyRentalItem> | null;

  // Initialization state
  isInitialized: boolean;

  // Actions
  initializeManagers: (
    db: Database,
    rentalsConfig: {
      getPath: (locationId: string) => string;
      parseData: (data: unknown) => AnyRentalItem[];
      categories: readonly string[];
    },
    stampsConfig: {
      getPath: (locationId: string) => string;
      parseData: (data: unknown) => AnyRentalItem[];
      categories: readonly string[];
    }
  ) => void;

  /** Subscribe to a location (lazy - only subscribes if not already subscribed) */
  subscribeToLocation: (board: BoardType, locationId: string) => void;

  /** Get cached data for a location */
  getCachedData: (board: BoardType, locationId: string) => LocationDataCache | null;

  /** Check if a location is loaded */
  isLocationLoaded: (board: BoardType, locationId: string) => boolean;

  /** Update cache for a location (called by listener manager) */
  updateLocationCache: (
    board: BoardType,
    locationId: string,
    items: AnyRentalItem[],
    categories: readonly string[]
  ) => void;

  /** Clean up all listeners */
  cleanup: () => void;

  /** Get stats about active listeners */
  getStats: () => { rentals: string[]; stamps: string[] };
}

/**
 * Organize flat items array into AllDataMap by category
 *
 * NOTE: This function dynamically creates buckets for any category encountered,
 * not just predefined ones. This supports custom categories created per-location
 * in Firestore. The tab display is controlled by the page's dynamic config,
 * so items in unlisted categories won't be visible (intentional orphan behavior).
 */
function organizeByCategory(
  items: AnyRentalItem[],
  categories: readonly string[]
): AllDataMap {
  const organized: AllDataMap = {} as AllDataMap;

  // Initialize known categories
  categories.forEach((category) => {
    organized[category as keyof AllDataMap] = [];
  });

  // Add items to their categories (create bucket if category not pre-initialized)
  items.forEach((item) => {
    const category = item.category as keyof AllDataMap;
    if (!organized[category]) {
      // Dynamic category not in predefined list - create bucket
      // This supports custom categories added per-location via admin panel
      organized[category] = [];
    }
    organized[category].push(item);
  });

  // Sort each category by sortOrder
  Object.keys(organized).forEach((category) => {
    organized[category as keyof AllDataMap].sort((a, b) => {
      const sortA = typeof a.sortOrder === 'number' && !isNaN(a.sortOrder) ? a.sortOrder : 999999;
      const sortB = typeof b.sortOrder === 'number' && !isNaN(b.sortOrder) ? b.sortOrder : 999999;
      return sortA - sortB;
    });
  });

  return organized;
}

/**
 * Validate linked group integrity and sanitize corrupted references.
 * Repairs client-side data only — never writes back to RTDB.
 *
 * Handles two corruption cases found in production:
 * 1. Parent references children that have no linkedItems metadata
 * 2. Child references a parent that doesn't include it in childIds
 */
function validateLinkedGroups(items: AnyRentalItem[]): AnyRentalItem[] {
  // Build lookup of all equipment items by ID
  const itemMap = new Map<string, RentalEquipmentItem>();
  for (const item of items) {
    if (item.type === 'item') {
      itemMap.set(item.id, item as RentalEquipmentItem);
    }
  }

  let hasRepairs = false;
  const repairedItems = items.map((item) => {
    if (item.type !== 'item') return item;

    const equip = item as RentalEquipmentItem;
    if (!equip.linkedItems) return item;

    if (equip.linkedItems.isParent) {
      // Validate parent: check each child exists, has matching groupId, and points back
      const childIds = equip.linkedItems.childIds || [];
      const validChildIds = childIds.filter((childId) => {
        const child = itemMap.get(childId);
        if (!child?.linkedItems) return false;
        if (child.linkedItems.groupId !== equip.linkedItems!.groupId) return false;
        if (child.linkedItems.parentId !== equip.id) return false;
        return true;
      });

      if (validChildIds.length === childIds.length) return item; // All valid

      hasRepairs = true;
      const removedCount = childIds.length - validChildIds.length;
      logger.warn(
        `[LinkedGroupValidation] Parent ${equip.id} (${equip.equipmentId}): removed ${removedCount} invalid child reference(s)`
      );

      if (validChildIds.length === 0) {
        // All children invalid — strip linkedItems entirely
        const { linkedItems: _, ...rest } = equip;
        return rest as AnyRentalItem;
      }

      // Some children invalid — filter childIds
      return {
        ...equip,
        linkedItems: { ...equip.linkedItems, childIds: validChildIds },
      } as AnyRentalItem;
    } else {
      // Validate child: check parent exists, has matching groupId, and includes this child
      const parentId = equip.linkedItems.parentId;
      if (!parentId) {
        hasRepairs = true;
        logger.warn(
          `[LinkedGroupValidation] Child ${equip.id} (${equip.equipmentId}): no parentId — removing linkedItems`
        );
        const { linkedItems: _, ...rest } = equip;
        return rest as AnyRentalItem;
      }

      const parent = itemMap.get(parentId);
      const isValid =
        parent?.linkedItems?.isParent &&
        parent.linkedItems.groupId === equip.linkedItems.groupId &&
        parent.linkedItems.childIds?.includes(equip.id);

      if (isValid) return item;

      hasRepairs = true;
      logger.warn(
        `[LinkedGroupValidation] Child ${equip.id} (${equip.equipmentId}): orphaned from parent ${parentId} — removing linkedItems`
      );
      const { linkedItems: _, ...rest } = equip;
      return rest as AnyRentalItem;
    }
  });

  if (hasRepairs) {
    logger.warn('[LinkedGroupValidation] Completed repairs on loaded data (client-side only)');
  }

  return repairedItems;
}

export const useRTDBCacheStore = create<RTDBCacheState>()(
  devtools(
    (set, get) => ({
      // Initial state
      rentalsCache: {},
      stampsCache: {},
      rentalsManager: null,
      stampsManager: null,
      isInitialized: false,

      initializeManagers: (db, rentalsConfig, stampsConfig) => {
        const state = get();

        // Don't re-initialize if already done
        if (state.isInitialized && state.rentalsManager?.isInitialized()) {
          return;
        }

        // Create rentals manager
        const rentalsManager = new RTDBListenerManager<AnyRentalItem>({
          getPath: rentalsConfig.getPath,
          parseData: rentalsConfig.parseData,
          boardName: 'Rentals',
          onDataChange: (items, locationId) => {
            get().updateLocationCache('rentals', locationId, items, rentalsConfig.categories);
          },
        });
        rentalsManager.initialize(db);

        // Create stamps manager
        const stampsManager = new RTDBListenerManager<AnyRentalItem>({
          getPath: stampsConfig.getPath,
          parseData: stampsConfig.parseData,
          boardName: 'Stamps',
          onDataChange: (items, locationId) => {
            get().updateLocationCache('stamps', locationId, items, stampsConfig.categories);
          },
        });
        stampsManager.initialize(db);

        set({
          rentalsManager,
          stampsManager,
          isInitialized: true,
        });
      },

      subscribeToLocation: (board, locationId) => {
        const { rentalsManager, stampsManager } = get();
        const manager = board === 'rentals' ? rentalsManager : stampsManager;

        if (!manager) {
          console.warn(`[RTDBCache] Cannot subscribe - ${board} manager not initialized`);
          return;
        }

        // Subscribe (manager handles deduplication)
        manager.subscribe(locationId);
      },

      getCachedData: (board, locationId) => {
        const cache = board === 'rentals' ? get().rentalsCache : get().stampsCache;
        return cache[locationId] || null;
      },

      isLocationLoaded: (board, locationId) => {
        const cache = get().getCachedData(board, locationId);
        return cache?.isLoaded ?? false;
      },

      updateLocationCache: (board, locationId, items, categories) => {
        const validatedItems = validateLinkedGroups(items);
        const organized = organizeByCategory(validatedItems, categories);
        const cacheEntry: LocationDataCache = {
          items: validatedItems,
          organized,
          lastUpdated: Date.now(),
          isLoaded: true,
        };

        if (board === 'rentals') {
          set((state) => ({
            rentalsCache: {
              ...state.rentalsCache,
              [locationId]: cacheEntry,
            },
          }));
        } else {
          set((state) => ({
            stampsCache: {
              ...state.stampsCache,
              [locationId]: cacheEntry,
            },
          }));
        }
      },

      cleanup: () => {
        const { rentalsManager, stampsManager } = get();
        rentalsManager?.cleanup();
        stampsManager?.cleanup();
        set({
          rentalsCache: {},
          stampsCache: {},
          rentalsManager: null,
          stampsManager: null,
          isInitialized: false,
        });
      },

      getStats: () => {
        const { rentalsManager, stampsManager } = get();
        return {
          rentals: rentalsManager?.getActiveLocations() || [],
          stamps: stampsManager?.getActiveLocations() || [],
        };
      },
    }),
    { name: 'RTDBCacheStore' }
  )
);
